// src/pages/admin/PaymentAnalysisPage.js
/**
 * 📊 PAGE ANALYSE DES PAIEMENTS - INTERFACE ADMIN
 * 
 * Dashboard de visualisation des problèmes de paiements
 * Bright Night 2025
 */

import React, { useState, useEffect } from 'react';
import './PaymentAnalysisPage.css';

const PaymentAnalysisPage = () => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(false);

    // Charger le rapport
    const loadReport = async () => {
        try {
            setLoading(true);
            const response = await fetch('./api/analysis/payments');
            
            if (!response.ok) {
                throw new Error('Erreur lors du chargement du rapport');
            }
            
            const data = await response.json();
            setReport(data.data);
            setLastRefresh(new Date());
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Charger au montage
    useEffect(() => {
        loadReport();
    }, []);

    // Auto-refresh toutes les 30 secondes
    useEffect(() => {
        if (!autoRefresh) return;
        
        const interval = setInterval(() => {
            loadReport();
        }, 30000);
        
        return () => clearInterval(interval);
    }, [autoRefresh]);

    // Format nombre
    const formatNumber = (num) => {
        return new Intl.NumberFormat('fr-FR').format(num);
    };

    // Format montant
    const formatAmount = (amount) => {
        return formatNumber(amount) + ' FCFA';
    };

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Format durée
    const formatDuration = (minutes) => {
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h ${mins}min`;
    };

    if (loading && !report) {
        return (
            <div className="analysis-page">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Chargement du rapport d'analyse...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="analysis-page">
                <div className="error-container">
                    <h2>❌ Erreur</h2>
                    <p>{error}</p>
                    <button onClick={loadReport} className="btn-retry">
                        Réessayer
                    </button>
                </div>
            </div>
        );
    }

    if (!report) return null;

    const { overview, pending_payments, duplicate_payments, success_without_ticket, tickets_without_payment, recommendations } = report;

    return (
        <div className="analysis-page">
            {/* Header */}
            <div className="analysis-header">
                <div className="header-content">
                    <h1>📊 Analyse des Paiements</h1>
                    <p className="subtitle">Guinness Bright Night 2025 - Dashboard Administrateur</p>
                </div>
                
                <div className="header-actions">
                    <div className="refresh-info">
                        <span className="last-refresh">
                            Dernière mise à jour : {lastRefresh ? formatDate(lastRefresh) : '-'}
                        </span>
                        <label className="auto-refresh-toggle">
                            <input 
                                type="checkbox" 
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                            />
                            <span>Auto-refresh (30s)</span>
                        </label>
                    </div>
                    
                    <button 
                        onClick={loadReport} 
                        className="btn-refresh"
                        disabled={loading}
                    >
                        {loading ? '⏳ Actualisation...' : '🔄 Actualiser'}
                    </button>
                </div>
            </div>

            {/* Vue d'ensemble */}
            <section className="overview-section">
                <h2 className="section-title">Vue d'ensemble</h2>
                
                <div className="stats-grid">
                    <div className="stat-card total">
                        <div className="stat-icon">💰</div>
                        <div className="stat-content">
                            <div className="stat-value">{formatAmount(overview.total_revenue)}</div>
                            <div className="stat-label">Total encaissé</div>
                        </div>
                    </div>
                    
                    <div className="stat-card success">
                        <div className="stat-icon">✅</div>
                        <div className="stat-content">
                            <div className="stat-value">{formatNumber(overview.success_count)}</div>
                            <div className="stat-label">Paiements réussis</div>
                        </div>
                    </div>
                    
                    <div className="stat-card pending">
                        <div className="stat-icon">⏳</div>
                        <div className="stat-content">
                            <div className="stat-value">{formatNumber(overview.pending_count)}</div>
                            <div className="stat-label">En attente</div>
                        </div>
                    </div>
                    
                    <div className="stat-card failed">
                        <div className="stat-icon">❌</div>
                        <div className="stat-content">
                            <div className="stat-value">{formatNumber(overview.failed_count)}</div>
                            <div className="stat-label">Échoués</div>
                        </div>
                    </div>
                </div>

                <div className="providers-grid">
                    {overview.by_provider.map((provider) => (
                        <div key={provider.method} className="provider-card">
                            <div className="provider-header">
                                <span className={`provider-icon ${provider.method}`}>
                                    {provider.method === 'mtn' ? '📱' : '🍊'}
                                </span>
                                <h3>{provider.method === 'mtn' ? 'MTN Mobile Money' : 'Orange Money'}</h3>
                            </div>
                            <div className="provider-stats">
                                <div className="provider-stat">
                                    <span className="label">Total paiements</span>
                                    <span className="value">{formatNumber(provider.total)}</span>
                                </div>
                                <div className="provider-stat">
                                    <span className="label">Réussis</span>
                                    <span className="value success-text">{formatNumber(provider.success)}</span>
                                </div>
                                <div className="provider-stat">
                                    <span className="label">Montant</span>
                                    <span className="value">{formatAmount(provider.total_amount)}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Recommandations */}
            {recommendations.length > 0 && (
                <section className="recommendations-section">
                    <h2 className="section-title">🎯 Actions recommandées</h2>
                    <div className="recommendations-list">
                        {recommendations.map((rec, index) => (
                            <div key={index} className={`recommendation-card priority-${rec.priority}`}>
                                <div className="rec-header">
                                    <span className="rec-badge">{rec.priority === 'critical' ? '🚨' : rec.priority === 'high' ? '⚠️' : 'ℹ️'}</span>
                                    <h3>{rec.title}</h3>
                                </div>
                                <p className="rec-action">{rec.action}</p>
                                {rec.command && (
                                    <code className="rec-command">{rec.command}</code>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Paiements en attente */}
            {pending_payments.length > 0 && (
                <section className="problems-section">
                    <h2 className="section-title">
                        ⏳ Paiements en attente ({pending_payments.length})
                    </h2>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Téléphone</th>
                                    <th>Montant</th>
                                    <th>Provider</th>
                                    <th>Transaction ID</th>
                                    <th>Statut</th>
                                    <th>Durée</th>
                                    <th>Alerte</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pending_payments.map((payment) => (
                                    <tr key={payment.id} className={payment.is_old ? 'row-alert' : ''}>
                                        <td>{payment.phone_masked}</td>
                                        <td>{formatAmount(payment.amount)}</td>
                                        <td>
                                            <span className={`badge badge-${payment.method}`}>
                                                {payment.method.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="code">{payment.transaction_id}</td>
                                        <td>
                                            <span className={`badge badge-${payment.status}`}>
                                                {payment.status}
                                            </span>
                                        </td>
                                        <td>{formatDuration(payment.minutes_ago)}</td>
                                        <td>
                                            {payment.is_old && (
                                                <span className="alert-badge">⚠️ Ancien</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Doubles paiements */}
            {duplicate_payments.length > 0 && (
                <section className="problems-section critical">
                    <h2 className="section-title">
                        🚨 Doubles paiements ({duplicate_payments.length})
                    </h2>
                    <div className="duplicates-list">
                        {duplicate_payments.map((dup, index) => (
                            <div key={index} className="duplicate-card">
                                <div className="dup-header">
                                    <div>
                                        <span className="dup-phone">{dup.phone_masked}</span>
                                        <span className="dup-amount">{formatAmount(dup.amount)}</span>
                                    </div>
                                    <span className="dup-count">{dup.payment_count} paiements</span>
                                </div>
                                
                                <div className="dup-payments">
                                    {dup.payments.map((payment) => (
                                        <div key={payment.payment_id} className="dup-payment">
                                            <div className="payment-info">
                                                <span className="payment-id">Payment #{payment.payment_id}</span>
                                                <span className="payment-date">{formatDate(payment.initiated_at)}</span>
                                            </div>
                                            <div className="payment-status">
                                                <span className={`badge badge-${payment.status}`}>
                                                    {payment.status}
                                                </span>
                                                {payment.has_ticket ? (
                                                    <span className="badge badge-success">
                                                        ✅ Ticket #{payment.ticket_id}
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-warning">
                                                        ❌ Pas de ticket
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="dup-action">
                                    ⚠️ <strong>Action requise :</strong> Rembourser le paiement sans ticket
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Paiements sans ticket */}
            {success_without_ticket.length > 0 && (
                <section className="problems-section">
                    <h2 className="section-title">
                        🎫 Paiements réussis sans ticket ({success_without_ticket.length})
                    </h2>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Téléphone</th>
                                    <th>Montant</th>
                                    <th>Provider</th>
                                    <th>Transaction ID</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {success_without_ticket.map((payment) => (
                                    <tr key={payment.id}>
                                        <td>#{payment.id}</td>
                                        <td>{payment.phone_masked}</td>
                                        <td>{formatAmount(payment.amount)}</td>
                                        <td>
                                            <span className={`badge badge-${payment.method}`}>
                                                {payment.method.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="code">{payment.transaction_id}</td>
                                        <td>{formatDate(payment.completed_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Tickets sans paiement */}
            {tickets_without_payment.length > 0 && (
                <section className="problems-section">
                    <h2 className="section-title">
                        💳 Tickets payants sans paiement ({tickets_without_payment.length})
                    </h2>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Code Ticket</th>
                                    <th>Type</th>
                                    <th>Téléphone</th>
                                    <th>Montant</th>
                                    <th>Date création</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets_without_payment.map((ticket) => (
                                    <tr key={ticket.id}>
                                        <td className="code">{ticket.ticket_code}</td>
                                        <td>
                                            <span className={`badge badge-${ticket.ticket_type}`}>
                                                {ticket.ticket_type_label}
                                            </span>
                                        </td>
                                        <td>{ticket.phone_masked}</td>
                                        <td>{formatAmount(ticket.purchase_amount)}</td>
                                        <td>{formatDate(ticket.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Footer */}
            <div className="analysis-footer">
                <p>Rapport généré le {formatDate(report.generated_at)} (Fuseau : {report.timezone})</p>
                <p>💡 Ce rapport est automatiquement mis à jour. Activez le mode auto-refresh pour un suivi en temps réel.</p>
            </div>
        </div>
    );
};

export default PaymentAnalysisPage;